import React, { useState, useCallback, useEffect } from 'react';
import { FaCheck, FaUsers, FaGift, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO.jsx';

const Prices = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    { id: 1, title: 'Initial consultation + Wellness Assessment', badge: 'Most Popular', description: 'Recommended for first visit', price: '£120', duration: '120min' },
    { id: 2, title: 'Stop Smoking', badge: null, description: 'Personalised bioresonance support', price: '£150', duration: '120min' },
    { id: 3, title: 'Swedish Massage', badge: null, description: 'Relaxation & muscle comfort', price: '£80', duration: '120min' },
    { id: 4, title: 'Food Sensitivity and Reactivity', badge: null, description: 'Everyday foods & ingredients', price: 'From £80', duration: '120min' },
    { id: 5, title: 'Seasonal pollens & environmental factors', badge: null, description: 'Pollens & outdoor factors', price: '£80', duration: '120min' },
    { id: 6, title: 'Targeted Energetic Stress Areas', badge: null, description: 'Focused individual options', price: '£80', duration: '120min' }
  ];

  // Detailed content for modal (per service)
  const detailContent = {
    1: {
      intro: 'A comprehensive session designed to provide a structured overview of your current bioenergetic stress patterns and help you identify the most appropriate direction for further support.',
      recommendedIf: [
        'experience ongoing fatigue, digestive discomfort, skin issues or other unexplained symptoms',
        'feel your situation is complex or unclear',
        'want a professional overview before choosing targeted options',
        'are unsure where to start'
      ],
      duringSession: [
        'BICOM BioResonance Method – working with your individual bioenergetic responses',
        'NLS analysis – a gentle, non-invasive insight into informational signals from the body',
        'Individual identification of priorities based on your current challenges',
        'Review of selected energetic stress patterns, which may include areas of parasitic, bacterial, viral or fungal character (from a bioenergetic perspective)'
      ],
      note: 'Each session is fully personalised and structured around your current needs.',
      youLeaveWith: [
        'a clearer understanding of your current energetic stress profile',
        'personalised wellness-focused guidance',
        'practical next steps tailored to your situation'
      ],
      singlePrice: '£120',
      singleLabel: 'Initial consultation + Wellness Assessment'
    },
    2: {
      intro: 'A focused session designed to support individuals who have decided to stop smoking and would like structured guidance in managing cravings and habitual patterns.',
      recommendedIf: [
        'feel ready to stop but struggle with cravings',
        'have previously attempted to quit',
        'want personalised, individual support'
      ],
      duringSession: [
        'BICOM BioResonance Method focused on patterns associated with smoking habits',
        'Identification of stress-related triggers connected to the habit',
        'Personalised bioresonance chip with mineral support',
        'Introduction to the Breathing Free Resistance Necklace',
        'Practical post-session guidance'
      ],
      note: 'For many individuals, one session is sufficient. Additional sessions may be considered depending on personal circumstances.',
      singlePrice: '£150',
      singleLabel: 'Stop Smoking'
    },
    3: {
      intro: 'A personalised massage session designed to support muscle comfort, relaxation and overall physical balance.',
      duringSession: [
        'Individual assessment of muscular tension',
        'Swedish massage techniques tailored to your condition',
        'Optional integration of BTL device support',
        'Use of selected aromatic oils',
        'Calm, structured environment supporting recovery'
      ],
      singlePrice: '£80',
      singleLabel: 'Swedish Massage'
    },
    4: {
      intro: 'A structured bioresonance session exploring energetic responses to selected foods and ingredients.',
      recommendedIf: [
        'experience bloating, digestive discomfort or irregular digestion',
        'notice changes in wellbeing after certain foods',
        'suspect food-related sensitivities'
      ],
      options: [
        { label: 'Basic (90 items) – Focused overview of commonly consumed foods.', price: '£80' },
        { label: 'Extended (200 items) – Broader, more detailed review including additional ingredients.', price: '£120' }
      ]
    },
    5: {
      intro: 'A structured session focused on energetic responses to seasonal pollens and environmental influences. Suitable for individuals experiencing seasonal or environmentally linked discomfort.',
      singlePrice: '£80',
      singleLabel: 'Pollens & outdoor factors (90)'
    },
    6: {
      intro: 'A targeted session focused on one selected energetic category. Recommended for returning clients or individuals who wish to explore a specific area only.',
      note: 'If you are unsure which option is most appropriate, a Full Assessment is recommended first.',
      options: [
        { label: 'Energetic Emotional Patterns (90)', price: '£80' },
        { label: 'Energetic parasitic patterns (90)', price: '£80' },
        { label: 'Energetic bacterial patterns (90)', price: '£80' },
        { label: 'Energetic viral patterns (90)', price: '£80' },
        { label: 'Energetic fungal patterns (90)', price: '£80' },
        { label: 'Lyme-related energetic patterns (90)', price: '£80' },
        { label: 'Energetic heavy metals patterns (90)', price: '£80' }
      ]
    }
  };

  const closeModal = useCallback(() => {
    setSelectedService(null);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedService) closeModal();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedService, closeModal]);


  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/10 to-gold/5">
      <SEO
        title="Price List"
        description="Price list for Bioresonance Leicester – initial consultation, wellness assessment, stop smoking, Swedish massage, food sensitivity and more. Transparent pricing at 108 New Walk, Leicester."
        path="/prices"
      />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              Price List – Leicester Wellness Centre
            </h1>
            <p className="text-xl text-dark-grey mb-4 leading-relaxed max-w-3xl mx-auto">
              Comprehensive wellness services designed to support your health journey.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-dark-grey">
              <div className="flex items-center">
                <FaUsers className="text-gold mr-2" />
                <span>Individual sessions</span>
              </div>
              <div className="flex items-center">
                <FaCheck className="text-gold mr-2" />
                <span>Transparent pricing</span>
              </div>
              <div className="flex items-center">
                <FaGift className="text-gold mr-2" />
                <span>Personalised care</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Prices – larger cards with character */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="group relative flex"
              >
                <div className="relative w-full">
                  {/* Main card container - clickable to open detail modal */}
                  <div className="relative p-4 h-full">
                    <article
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedService(service.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedService(service.id); } }}
                      className="h-full min-h-[320px] bg-white rounded-xl border border-grey/20 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
                      aria-label={`View details for ${service.title}`}
                    >
                      {/* Decorative corner accent */}
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-gold/10 to-transparent rounded-bl-full" aria-hidden />
                      
                      {/* Content */}
                      <div className="relative z-10 p-6 flex-1 flex flex-col">
                        {/* Badge */}
                        {service.badge && (
                          <div className="mb-3">
                            <span className="inline-block text-xs uppercase tracking-wider text-gold bg-gold/20 px-3 py-1 rounded-full font-bold">
                              {service.badge}
                            </span>
                          </div>
                        )}
                        
                        {/* Title */}
                        <h2 className="text-xl md:text-2xl font-bold text-dark-grey leading-tight mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {service.title}
                        </h2>
                        
                        {/* Description */}
                        <p className="text-base text-dark-grey leading-relaxed mb-5 flex-1">
                          {service.description}
                        </p>
                        
                        {/* Price section */}
                        <div className="pt-4 border-t border-grey/50 mt-auto">
                          <div className="flex items-baseline justify-between gap-3 mb-4">
                            <span className="text-3xl font-bold text-gold tabular-nums" style={{ fontFamily: 'Playfair Display, serif' }}>
                              {service.price}
                            </span>
                            <span className="text-sm text-dark-grey font-semibold">{service.duration}</span>
                          </div>
                          
                          {/* Book button - links to SumUp bookings */}
                          <a
                            href="https://www.sumupbookings.com/t-p-wellness-ltd"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="group/btn block bg-gold/15 hover:bg-gold rounded-lg py-3 px-4 text-center transition-all duration-300 transform hover:scale-[1.02] border border-gold/30 hover:border-gold"
                          >
                            <span className="text-base font-bold text-gold group-hover/btn:text-black transition-colors duration-300">
                              Book Appointment →
                            </span>
                          </a>
                        </div>
                      </div>
                      
                      {/* Decorative bottom accent */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden />
                    </article>
                    
                    {/* Creative border - same as Therapy page */}
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500 pointer-events-none"></div>
                    <div className="absolute inset-2 border-2 border-white/50 rounded-xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500 pointer-events-none"></div>
                  </div>
                  
                  {/* Floating elements */}
                  <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                  <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Information Section */}
      <section className="py-16 bg-white/80">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-dark-grey mb-8 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
              <FaInfoCircle className="text-gold text-2xl" />
              Important Information
            </h2>
            <ul className="space-y-4 text-dark-grey text-lg leading-relaxed">
              <li>Wellness Assessment is a non-medical evaluation of wellbeing and balance.</li>
              <li>Wellness sessions are usually scheduled once per week, depending on individual needs.</li>
              <li>Packages are valid for 3 months, unless otherwise agreed.</li>
              <li>Services are wellness-focused and do not replace medical treatment.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Service detail modal (same style as home page badge modal) */}
      <AnimatePresence>
        {selectedService && detailContent[selectedService] && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
              onClick={closeModal}
              aria-hidden
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border-4 border-gold/30">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gold/10 to-transparent rounded-bl-full" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue/10 to-transparent rounded-tr-full" />

                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-gold text-dark-grey hover:text-white rounded-full p-2 transition-all duration-300 hover:scale-110 shadow-lg"
                  aria-label="Close"
                >
                  <FaTimes className="w-5 h-5" />
                </button>

                <div className="relative p-6 md:p-8 overflow-y-auto max-h-[90vh]">
                  <h2 className="text-2xl md:text-3xl font-bold text-dark-grey mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {services.find(s => s.id === selectedService)?.title}
                  </h2>
                  <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent w-full max-w-xs mb-6" />

                  <h3 className="text-lg font-bold text-dark-grey mb-2">What is included?</h3>
                  <p className="text-dark-grey leading-relaxed mb-4">
                    {detailContent[selectedService].intro}
                  </p>

                  {detailContent[selectedService].recommendedIf && (
                    <>
                      <p className="text-dark-grey font-semibold mb-2">This session is recommended if you:</p>
                      <ul className="list-disc list-inside text-dark-grey space-y-1 mb-4 pl-2">
                        {detailContent[selectedService].recommendedIf.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {detailContent[selectedService].duringSession && (
                    <>
                      <p className="text-dark-grey font-semibold mb-2">During your session:</p>
                      <ul className="list-disc list-inside text-dark-grey space-y-1 mb-4 pl-2">
                        {detailContent[selectedService].duringSession.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {detailContent[selectedService].note && (
                    <p className="text-dark-grey leading-relaxed mb-4 italic">
                      {detailContent[selectedService].note}
                    </p>
                  )}

                  {detailContent[selectedService].youLeaveWith && (
                    <>
                      <p className="text-dark-grey font-semibold mb-2">You leave with:</p>
                      <ul className="list-disc list-inside text-dark-grey space-y-1 mb-4 pl-2">
                        {detailContent[selectedService].youLeaveWith.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {detailContent[selectedService].options ? (
                    <>
                      <p className="text-dark-grey font-semibold mb-3">Available options:</p>
                      <div className="space-y-3">
                        {detailContent[selectedService].options.map((opt, i) => (
                          <div key={i} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-grey/30 last:border-0">
                            <p className="text-dark-grey flex-1">{opt.label}</p>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-gold">{opt.price}</span>
                              <a
                                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={closeModal}
                                className="bg-gold hover:bg-gold/90 text-black font-semibold py-2 px-4 rounded-lg transition-colors shrink-0"
                              >
                                Book
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="mt-6 pt-4 border-t border-grey/30 flex flex-wrap items-center justify-between gap-4">
                      <span className="text-2xl font-bold text-gold" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {detailContent[selectedService].singlePrice}
                      </span>
                      <a
                        href="https://www.sumupbookings.com/t-p-wellness-ltd"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeModal}
                        className="bg-gold hover:bg-gold/90 text-black font-bold py-3 px-6 rounded-lg transition-colors"
                      >
                        Book
                      </a>
                    </div>
                  )}

                  <div className="mt-8 flex justify-center gap-2">
                    <div className="w-2 h-2 bg-gold rounded-full" />
                    <div className="w-2 h-2 bg-blue rounded-full" />
                    <div className="w-2 h-2 bg-gold rounded-full" />
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default Prices;
